import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Send, MessageSquare } from "lucide-react";
import { useBrand } from "../context/BrandContext";

export function ChangeMindDialog({ open, onOpenChange, status, comments, currentUser, onSubmit, submitting }) {
  const brand = useBrand();
  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open) scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [comments.length, open]);

  const submit = async () => {
    if (!text.trim() || submitting) return;
    await onSubmit(text.trim());
    setText("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-xl" data-testid="change-mind-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-black tracking-tighter flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-[var(--brand-primary)]" /> Changed your mind?
          </DialogTitle>
          <DialogDescription className="text-neutral-500 pt-1">
            This article is locked as{" "}
            <span className={`font-semibold ${status === "rejected" ? "text-red-600" : "text-green-700"}`}>
              {status === "rejected" ? "rejected" : "approved"}
            </span>{" "}
            and the decision can't be reversed here — but our team can help. Leave a message below and
            we'll follow up with you directly.
          </DialogDescription>
        </DialogHeader>

        <div ref={scrollRef} className="max-h-64 overflow-y-auto space-y-2.5 pr-1" data-testid="change-mind-messages">
          {comments.length === 0 ? (
            <p className="text-xs text-neutral-400" data-testid="change-mind-empty-message">
              No messages yet — tell us what you'd like to change.
            </p>
          ) : (
            comments.map((c) => {
              const own = c.author_username === currentUser;
              return (
                <div key={c.id} data-testid={`change-mind-item-${c.id}`} className={`flex ${own ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-xl px-3 py-2 ${own ? "bg-[var(--brand-primary)] text-white rounded-br-sm" : "bg-neutral-100 text-neutral-800 rounded-bl-sm"}`}>
                    {!own && <p className="text-[10px] font-semibold text-neutral-500 mb-0.5">{c.author_name}</p>}
                    <p className="text-xs leading-relaxed whitespace-pre-wrap">{c.text}</p>
                    <p className={`font-mono-accent text-[9px] mt-1 ${own ? "text-white/60" : "text-neutral-400"}`}>
                      {new Date(c.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="flex items-end gap-2 pt-1">
          <textarea
            data-testid="change-mind-text-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            rows={2}
            placeholder="e.g. We approved this too early — please hold publication..."
            className="flex-1 text-sm border border-black/10 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-[#1058A8]/40 resize-none bg-white"
          />
          <Button
            data-testid="change-mind-submit-button"
            size="sm"
            disabled={!text.trim() || submitting}
            onClick={submit}
            className="rounded-full h-9 w-9 p-0 bg-[var(--brand-primary)] hover:opacity-90 text-white active:scale-95 transition-colors"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-[11px] text-neutral-400">
          Your message is logged with this article and emailed to the {brand.display_name} team.
        </p>
      </DialogContent>
    </Dialog>
  );
}
